import { assistantSkills, type AssistantSkill } from '@/app/lib/assistant-v2/skills/catalog';

type SkillId = typeof assistantSkills[number]['id'];

// Examples prepare a draft. They do not grant authority or select a runtime tool.
export const skillStartingPrompts = {
  academic_advising: 'Help me think through an academic decision and prepare my next steps.',
  academic_policy: 'Help me find the official Rutgers policy that applies to my question.',
  registration: 'Help me diagnose a registration problem and check my alternatives.',
  financial_aid_navigation: 'Help me find official financial aid guidance and prepare questions for the office.',
  career_and_research: 'Help me find Rutgers research opportunities that match my interests.',
  administrative_forms: 'Help me locate an official form and prepare a checklist for completing it.',
  international_student_navigation: 'Help me find the Rutgers Global process and the right office for my question.',
  wellbeing_and_support: 'Help me find the right Rutgers student support resource.',
  web_research: 'Research my question using current official sources and make a brief with citations.',
  campus_life: 'Help me plan a campus day around my classes, meals, and travel.',
  course_search: 'Help me find courses and verify their sections for my term.',
  degree_planning: 'Review my selected degree plan and help me understand the remaining requirements.',
  first_day_navigator: 'Use my selected schedule to prepare a next-class travel brief, labeling travel estimates.',
  graduation_roadmap: 'Prepare a draft graduation roadmap with prerequisites and offering uncertainty made clear.',
  registration_readiness: 'Prepare a registration checklist with section indexes, conflicts, and backups.',
  registration_rescue: 'Help me find verified alternatives for a closed or conflicting section.',
  schedule_repair: 'Help me repair my selected schedule while preserving my constraints.',
  scheduling: 'Help me build schedule options for my courses and time preferences.',
} satisfies Record<SkillId, string>;

export function filterSkillLibrary(query: string): readonly AssistantSkill[] {
  const terms = query.toLowerCase().trim().replace(/^\$/, '').split(/\s+/).filter(Boolean);
  return assistantSkills.filter((skill) => {
    const text = `${skill.id.replaceAll('_', ' ')} ${skill.id} ${skill.name} ${skill.description} ${skillStartingPrompts[skill.id]}`.toLowerCase();
    return terms.every((term) => text.includes(term));
  });
}

export function prepareSkillDraft(skill: AssistantSkill, draft: string): string {
  const mention = `$${skill.id}`;
  if (draft.split(/\s+/).includes(mention)) return draft;
  // Preserve the complete existing draft, including line breaks and spacing.
  return `${mention} ${draft || skillStartingPrompts[skill.id as SkillId] || ''}`;
}
